// #ux-redesign-start: Button primitive (Paso 2)
// Botón del DS. Reemplaza el uso directo de Reactstrap Button con variantes
// semánticas tipadas. Mantiene focus visible y loading state nativos.
//
// PORTABILIDAD: trasplantable a Web-Patients sin cambios.
//
// USO:
//   <Button variant="primary" size="md" onClick={...}>Speichern</Button>
//   <Button variant="secondary" onClick={...}>Abbrechen</Button>
//   <Button variant="danger" loading={isDeleting} onClick={...}>Löschen</Button>
//   <Button variant="tertiary" size="sm" as="a" href="...">Mehr</Button>
//
// Variants:
//   - primary:   azul, CTA principal
//   - secondary: outline, accion neutra (reemplaza el "Cancel naranja" actual)
//   - tertiary:  ghost, links/acciones de bajo peso
//   - danger:    rojo, accion destructiva (reemplaza el "Delete naranja")
//
// Sizes: sm (28px) / md (36px, default) / lg (44px)
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';

const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  type = 'button',
  loading = false,
  disabled = false,
  fullWidth = false,
  iconLeft = null,
  iconRight = null,
  className = '',
  children,
  as: Tag = 'button',
  ...rest
}, ref) => {
  const isDisabled = disabled || loading;
  const classes = [
    'ds-btn',
    `ds-btn--${variant}`,
    `ds-btn--${size}`,
    fullWidth ? 'ds-btn--full' : '',
    loading ? 'ds-btn--loading' : '',
    className,
  ].filter(Boolean).join(' ');

  const tagProps = Tag === 'button' ? { type, disabled: isDisabled } : { 'aria-disabled': isDisabled };

  return (
    <Tag
      ref={ref}
      className={classes}
      {...tagProps}
      {...rest}
    >
      {loading && (
        <span className="ds-btn__spinner" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </span>
      )}
      {!loading && iconLeft && <span className="ds-btn__icon ds-btn__icon--left">{iconLeft}</span>}
      <span className="ds-btn__label">{children}</span>
      {!loading && iconRight && <span className="ds-btn__icon ds-btn__icon--right">{iconRight}</span>}
    </Tag>
  );
});

Button.displayName = 'Button';

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'danger']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  type: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
  as: PropTypes.elementType,
};

export default Button;
// #ux-redesign-end
